import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react-native';
import {
  Box,
  HStack,
  Icon,
  Input,
  InputField,
  Pressable,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useReferenceSearch } from './useReferenceSearch';
import ReferenceFieldBox from './ReferenceFieldBox';
import ReferencePill from './ReferencePill';
import type { ReferenceListFieldProps, ReferenceOption } from './types';

const shortId = (id: string): string => (id.length > 8 ? `${id.slice(0, 8)}…` : id);

/**
 * ReferenceListField
 * ----
 * Multi-select reference editor: chips, debounced server search, inline retry on failure.
 */
const ReferenceListField: React.FC<ReferenceListFieldProps> = ({
  value,
  onChange,
  objectName,
  fixedFilter,
  searchField = 'title',
  limit = 8,
  getDisabledReason,
  testID = 'reference-list-field',
  searchPlaceholderKey = 'admin.common.references.searchPlaceholder',
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const selectedIds = useMemo(() => value.map((item) => item.id), [value]);

  const {
    options,
    isLoading,
    error,
    retry,
  } = useReferenceSearch({
    objectName,
    query,
    fixedFilter,
    searchField,
    limit,
    excludeIds: selectedIds,
  });

  const chipLabel = (item: ReferenceOption): string => {
    if (item.title) return item.title;
    return t('admin.common.references.unknownLabel', {
      id: shortId(item.id),
    });
  };

  const addOption = (option: ReferenceOption) => {
    if (selectedIds.includes(option.id)) return;
    const reason = getDisabledReason?.(option);
    if (reason) return;
    onChange([...value, { id: option.id, title: option.title ?? undefined }]);
    setQuery('');
  };

  const removeOption = (id: string) => {
    onChange(value.filter((item) => item.id !== id));
  };

  const showResults = query.trim().length > 0;

  return (
    <VStack space="sm" testID={testID}>
      <ReferenceFieldBox>
        {value.map((item) => (
          <ReferencePill
            key={item.id}
            testID={`reference-chip-${item.id}`}
            label={chipLabel(item)}
            onRemove={() => removeOption(item.id)}
            removeTestID={`reference-unpin-${item.id}`}
            removeLabel={t('admin.common.references.remove')}
          />
        ))}
        <Input
          flex={1}
          minWidth={120}
          h="auto"
          borderWidth={0}
          bg="transparent"
          px="$0"
          py="$0"
          alignSelf="center"
        >
          <InputField
            testID="reference-search"
            value={query}
            onChangeText={setQuery}
            placeholder={t(searchPlaceholderKey)}
            px="$1"
            py="$0"
            h="auto"
            fontSize="$xs"
            lineHeight="$sm"
          />
        </Input>
      </ReferenceFieldBox>

      {!!error && (
        <Pressable
          testID="reference-retry"
          onPress={retry}
          px="$3"
          py="$2"
          bg="$statusErrorSubtle"
          borderRadius="$md"
        >
          <Text size="sm" color="$statusError">
            {t('admin.common.references.retry')}
          </Text>
        </Pressable>
      )}

      {showResults && !error ? (
        isLoading ? (
          <Spinner size="small" testID="reference-search-loading" />
        ) : (
          <VStack
            borderWidth={1}
            borderColor="$borderLight"
            borderRadius="$md"
            overflow="hidden"
          >
            {options.slice(0, limit).map((option, index) => {
              const disabledReason = getDisabledReason?.(option) ?? null;
              const isDisabled = !!disabledReason;
              return (
                <Pressable
                  key={option.id}
                  testID={
                    isDisabled
                      ? `reference-row-disabled-${option.id}`
                      : `reference-add-${option.id}`
                  }
                  onPress={() => addOption(option)}
                  disabled={isDisabled}
                  px="$3"
                  py="$2"
                  borderBottomWidth={index < Math.min(options.length, limit) - 1 ? 1 : 0}
                  borderBottomColor="$borderLight"
                  $hover={{ bg: isDisabled ? undefined : '$bgHover' }}
                  opacity={isDisabled ? 0.6 : 1}
                >
                  <HStack justifyContent="space-between" alignItems="center">
                    <VStack flex={1} pr="$2">
                      <Text size="sm" color="$textBody" numberOfLines={1}>
                        {option.title ?? t('admin.common.references.unknownLabel', {
                          id: shortId(option.id),
                        })}
                      </Text>
                      {!!disabledReason && (
                        <Text size="xs" color="$textSubtle">
                          {disabledReason}
                        </Text>
                      )}
                    </VStack>
                    {isDisabled ? null : <Icon as={Plus} size="sm" color="$brandBase" />}
                  </HStack>
                </Pressable>
              );
            })}
            {options.length === 0 && !isLoading ? (
              <Box px="$3" py="$2">
                <Text size="sm" color="$textSubtle">
                  {t('admin.common.references.noResults')}
                </Text>
              </Box>
            ) : null}
          </VStack>
        )
      ) : null}
    </VStack>
  );
};

export default ReferenceListField;
